import * as React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/pro-solid-svg-icons";

import _isString from "lodash/isString";
import _isFunction from "lodash/isFunction";

import { composeComparators } from "../helpers/array";
import * as defaultStyles from "../styles/components/Datatable.css";

const SORT_STATES = {
    unsorted: {
        next: "ascending",
        sorted: false,
        icon: faSort
    },
    ascending: {
        next: "descending",
        sorted: true,
        reversed: false,
        icon: faSortUp
    },
    descending: {
        next: "unsorted",
        sorted: true,
        reversed: true,
        icon: faSortDown
    }
};

class Datatable extends React.Component {
    constructor(props) {
        super(props);
        this.state = this.initialSortState(props);
        this.addSortMethod = this.addSortMethod.bind(this);
    }

    initialSortState(props) {
        return {
            sortState: props.columns.map(column => SORT_STATES["unsorted"])
        };
    }

    static createColumn({
        disabled,
        renderHeaderCell,
        renderBodyCell,
        cellWidthPercentage,
        comparator
    }) {
        return {
            disabled,
            renderHeaderCell,
            renderBodyCell,
            cellWidthPercentage,
            comparator
        };
    }

    addSortMethod(columnIndex) {
        this.setState((state, props) => {
            const sortState = props.allowMultiColumnSort
                ? [...state.sortState]
                : this.initialSortState(props).sortState;

            // advance the clicked column to its next sort state in the new sortState array
            const prevColumnSortState = state.sortState[columnIndex];
            sortState[columnIndex] = SORT_STATES[prevColumnSortState.next];

            return { sortState };
        });
    }

    comparatorObjectForColumn(columnSortState, columnIndex) {
        return !columnSortState.sorted
            ? null
            : {
                  comparator: this.props.columns[columnIndex].comparator,
                  reversed: columnSortState.reversed
              };
    }

    functionPropertyIfPresent(property) {
        const fn = this.props[property];
        if (fn) {
            return fn;
        }
        return () => {};
    }

    render() {
        const { className, data, showHeaders, children } = this.props;
        const { sortState } = this.state;

        // don't include disabled columns
        const columns = this.props.columns.filter(column => !column.disabled);

        // create the comparator objects for the composeComparators call
        const compareObjects = sortState
            .map((state, index) => this.comparatorObjectForColumn(state, index))
            .filter(Boolean);

        // if there are no sorted columns, return the data in its original order. otherwise make a copy of that data
        // and sort it
        const sortedData =
            compareObjects.length === 0
                ? data
                : [...data].sort(composeComparators(compareObjects));

        const baseStyle = {
            table: [defaultStyles.dataTable, className]
                .filter(Boolean)
                .join(" "),
            header: {
                base: defaultStyles.tableHeader,
                cell: this.props.headerCell
                    ? this.props.headerCell
                    : defaultStyles.headerCell,
                cellContent: [
                    defaultStyles.headerCellContent,
                    defaultStyles.thInner
                ]
                    .filter(Boolean)
                    .join(" "),
                sortIcon: defaultStyles.headerCellSortIcon
            },
            row: {
                base: defaultStyles.row,
                cell: this.props.rowCell
                    ? this.props.rowCell
                    : defaultStyles.rowCell
            }
        };

        const containerHeight = this.props.scroll
            ? { height: this.props.scroll.height }
            : {};
        return (
            <div
                className={showHeaders && defaultStyles.fixedTableContainer}
                style={{ ...containerHeight, ...(this.props.style || {}) }}
            >
                {/*
                    The .headerBackground is a quirk of how we get the fixed header. The header cells are
                    pushed above the actual header row, and don't fill it entirely, so the .headerBackground
                    class provides a way to change the header color as it can't just be done in the cells on
                    when the header is fixed
                */}
                {showHeaders &&
                    this.props.scroll && (
                        <div className={defaultStyles.headerBackground} />
                    )}
                <div className={defaultStyles.fixedTableContainerInner}>
                    <table
                        cellSpacing={0}
                        className={baseStyle.table}
                        style={this.props.style ? this.props.style : {}}
                    >
                        <thead>
                            {showHeaders &&
                                this.renderTableHeader(
                                    baseStyle,
                                    columns,
                                    sortState
                                )}
                        </thead>
                        <tbody>
                            {this.renderTableBody(
                                baseStyle,
                                columns,
                                sortedData
                            )}
                        </tbody>
                    </table>
                    {children}
                </div>
            </div>
        );
    }

    renderTableHeader(baseStyle, columns, sortState) {
        return (
            <tr className={baseStyle.header.base}>
                {columns.map((column, columnIndex) =>
                    this.renderTableHeaderCell(
                        baseStyle,
                        column,
                        columnIndex,
                        sortState
                    )
                )}
            </tr>
        );
    }

    renderTableHeaderCell(baseStyle, column, columnIndex, sortState) {
        const defaultCellStyle = {
            width: `${column.cellWidthPercentage}%`
        };

        const headerClickHandler =
            column.comparator &&
            (() => {
                if (this.props.onSortAsync) {
                    return this.props
                        .onSortAsync(columnIndex, column)
                        .then(
                            this.addSortMethod(columnIndex, column.comparator)
                        );
                }
                if (this.props.onSort) {
                    this.props.onSort(columnIndex, column);
                }
                this.addSortMethod(columnIndex, column.comparator);
            });

        const headerStyle = column.comparator
            ? {
                  ...defaultCellStyle,
                  cursor: "pointer"
              }
            : defaultCellStyle;

        const headerIcon = column.comparator && (
            <FontAwesomeIcon
                className={baseStyle.header.sortIcon}
                icon={sortState[columnIndex].icon}
            />
        );

        return (
            <th
                className={baseStyle.header.cell}
                key={`header-${columnIndex}`}
                onClick={headerClickHandler}
                style={headerStyle}
            >
                <div
                    className={baseStyle.header.cellContent}
                    style={defaultCellStyle}
                >
                    {column.renderHeaderCell()} {headerIcon}
                </div>
            </th>
        );
    }

    rowClassName(rowIndex, rowDatum) {
        if (this.props.rowClassName) {
            if (_isString(this.props.rowClassName)) {
                return this.props.rowClassName;
            }
            if (_isFunction(this.props.rowClassName)) {
                return this.props.rowClassName(rowIndex, rowDatum);
            }
        }
        return "";
    }

    renderTableBody(baseStyle, columns, sortedData) {
        return sortedData.map((rowDatum, rowIndex) => {
            const rowClassName = `${baseStyle.row.base} ${this.rowClassName(
                rowIndex,
                rowDatum
            )}`;
            const rowStyle = this.props.onRowClick
                ? { cursor: "pointer" }
                : null;

            const onMouseEnterRow = this.functionPropertyIfPresent(
                "onMouseEnterRow"
            );
            const onMouseLeaveRow = this.functionPropertyIfPresent(
                "onMouseLeaveRow"
            );
            const onRowClick = this.functionPropertyIfPresent("onRowClick");
            return (
                <tr
                    onMouseEnter={() => onMouseEnterRow(rowIndex, rowDatum)}
                    onMouseLeave={() => onMouseLeaveRow(rowIndex, rowDatum)}
                    onClick={() => onRowClick(rowIndex, rowDatum)}
                    style={rowStyle}
                    className={rowClassName}
                    key={`row(${rowIndex})`}
                >
                    {columns.map((column, colIndex) =>
                        this.renderTableBodyCell(
                            column,
                            rowIndex,
                            colIndex,
                            rowDatum,
                            baseStyle
                        )
                    )}
                </tr>
            );
        });
    }

    columnClassName(columnIndex, rowIndex, rowDatum) {
        if (this.props.columnClassName) {
            if (_isString(this.props.columnClassName)) {
                return this.props.columnClassName;
            }
            if (_isFunction(this.props.columnClassName)) {
                return this.props.columnClassName(
                    columnIndex,
                    rowIndex,
                    rowDatum
                );
            }
        }
        return "";
    }

    renderTableBodyCell(column, rowIndex, colIndex, rowDatum, baseStyle) {
        const { renderBodyCell } = column;
        const defaultCellStyle = {
            width: `${column.cellWidthPercentage}%`
        };

        const onMouseEnterCell = this.functionPropertyIfPresent(
            "onMouseEnterCell"
        );
        const onMouseLeaveCell = this.functionPropertyIfPresent(
            "onMouseLeaveCell"
        );
        const onCellClick = this.functionPropertyIfPresent("onCellClick");

        const cellStyle = this.props.onCellClick
            ? {
                  ...defaultCellStyle,
                  cursor: "pointer"
              }
            : defaultCellStyle;

        const cellClassName = `${baseStyle.row.cell} ${this.columnClassName(
            colIndex,
            rowIndex,
            rowDatum
        )}`;
        return (
            <td
                onMouseEnter={() =>
                    onMouseEnterCell(rowIndex, colIndex, rowDatum, column)
                }
                onMouseLeave={() =>
                    onMouseLeaveCell(rowIndex, colIndex, rowDatum, column)
                }
                onClick={() =>
                    onCellClick(rowIndex, colIndex, rowDatum, column)
                }
                style={cellStyle}
                className={cellClassName}
                key={`col(${colIndex})`}
            >
                {renderBodyCell(rowDatum, rowIndex)}
            </td>
        );
    }
}

Datatable.propTypes = {
    // Basic class name, added to the root tables
    className: PropTypes.string,
    /*
        These are the data columns:
          renderHeaderCell: (data)=> string | component
            Renders the data to the th cell in thead
          renderBodyCell: (data, rowIndex) => string | component
            Renders data to the td in tbody
          cellWidthPercentage: number
            the percentage that the given cell will take up in the table
          comparator: optional (a,b)=>number
            if passed, will allow sorting by this column

     */
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            disabled: PropTypes.bool,
            renderHeaderCell: PropTypes.func.isRequired,
            renderBodyCell: PropTypes.func.isRequired,
            cellWidthPercentage: PropTypes.number.isRequired,
            comparator: PropTypes.oneOfType([PropTypes.func, PropTypes.string])
        })
    ).isRequired,
    /*
      Data is an array of whatever data you want to show
     */
    data: PropTypes.array.isRequired,

    /*
      A basic styles object. Will be applied inline to the root table object
     */
    style: PropTypes.object,

    /*
     Style Overrides
     headerCell is a css class that over-rides the current headerCell class
     rowCell is a css class name that overrides the current rowCell class
     */
    headerCell: PropTypes.string,
    rowCell: PropTypes.string,

    /*
      Row Action Callbacks:
        All of the following row callbacks that accept the following parameters:
          (rowIndex, rowData)
     */
    onRowClick: PropTypes.func,
    onMouseEnterRow: PropTypes.func,
    onMouseLeaveRow: PropTypes.func,

    /*
      Cell action callbacks:
        The following callbacks that accept the following parameters:
          (rowIndex, colIndex, rowDatum, column)
     */
    onCellClick: PropTypes.func,
    onMouseEnterCell: PropTypes.func,
    onMouseLeaveCell: PropTypes.func,
    /*
      onSort and onSortAsync cannot be used together, as onSortAsync will override it.

      When the table is sorted, they are called with the following parameters (columnIndex, column)

      onSortAsync does not trigger the actual sort until the you resolve a promise from the
        onSortAsync function
     */
    onSort: PropTypes.func,
    onSortAsync: PropTypes.func,
    /*
      Allows for a fixed header table. Height is the height of the table,
      and can be any valid css `height` value
    */
    scroll: PropTypes.shape({
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired
    }),

    /*
       rowClassName allows you to add classes to body rows.

       If the value is a string, it will be appended to the className property for every <tr> element in the table's
       body.

       If the value is a function, the signature is assumed to be (rowIndex, rowDatum) => string. The result will be
       treated as if it were a string value as above.
     */
    rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    /*
       columnClassName allows you to add classes to body cells.

       If the value is a string, it will be appended to the className property for every <td> element in the table's
       body.

       If the value is a function, the signature is assumed to be (columnIndex, rowIndex, rowDatum) => string. The
       result will be treated as if it were a string value as above.
     */
    columnClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    allowMultiColumnSort: PropTypes.bool,

    showHeaders: PropTypes.bool,

    children: PropTypes.node
};

Datatable.defaultProps = {
    allowMultiColumnSort: false,
    showHeaders: true
};

export default Datatable;



// WEBPACK FOOTER //
// ./src/components/Datatable.js